import { companyAPI, reportAPI } from '@/services-ts/axios'
import type {
  IGetCompanyObjectivesList,
  IGetCompanyProgress,
  IGetObjectiveProgress,
  IGetPerformanceBestTeam,
  IGetPerformanceEmployeeSuccess,
  IGetPerformanceReportCards,
  IGetPerformanceWeeklyItemReport,
  IGetTeamMember,
  IPayloadCompanyObjective,
  IPayloadTeam,
  IPerformanceList,
  IUpdatePerformanceSettings,
  IUpdateTeamMember,
} from '@/types/Request'
import type { IPerformanceStore } from '@/types/Store/State'
import { Alert, Format } from '@/utils-ts'
import { defineStore } from 'pinia'

export const usePerformanceStore = defineStore('performance', {
  state: (): IPerformanceStore => ({
    settings: {
      id: '',
      isCompanyValueRequired: false,
      isObjectiveNameManual: false,
      allowSubmission: false,
      allowTeamOutsideTagging: false,
      isPointRequired: false,
      isRecipientTaggingRequired: false,
    },
    companyObjectives: [],
    teams: [],
    objectives: [],
    teamMembers: [],
    weeklyItems: [],
    keyResults: [],
  }),

  actions: {
    // #region Settings
    async GetPerformanceSettings() {
      const { data, status } = await companyAPI.PerformanceServices.GetPerformanceSettings()
      if (status) this.settings = data
      return data
    },

    async UpdatePerformanceSettings(payload: IUpdatePerformanceSettings) {
      const { status } = await companyAPI.PerformanceServices.UpdatePerformanceSettings(payload)

      if (status) {
        this.settings = { ...this.settings, ...payload }
        void Alert({
          message: Format('admin_global_updated'),
          type: 'success',
        })
      }
      return status
    },
    // #endregion

    // #region Company Objectives
    async CreateCompanyObjective(payload: IPayloadCompanyObjective) {
      const { status, data } = await companyAPI.PerformanceServices.CreateCompanyObjective(payload)

      if (status) {
        this.companyObjectives.unshift(data)
        void Alert({
          message: Format('admin_global_created'),
          type: 'success',
        })
      }

      return status
    },

    async UpdateCompanyObjective(payload: IPayloadCompanyObjective) {
      const { status } = await companyAPI.PerformanceServices.UpdateCompanyObjective(payload)
      if (status) {
        const index = this.companyObjectives.findIndex(objective => objective.id === payload.id)
        const objective = this.companyObjectives[index]
        if (objective) {
          Object.assign(objective, payload)
        }
        void Alert({
          message: Format('admin_global_updated'),
          type: 'success',
        })
      }
      return status
    },

    async GetCompanyObjectives(payload: IGetCompanyObjectivesList) {
      const { status, data } = await companyAPI.PerformanceServices.GetCompanyObjectives(payload)
      this.companyObjectives = status ? data : []
    },

    async DeleteCompanyObjective(id: string) {
      const { status } = await companyAPI.PerformanceServices.DeleteCompanyObjective(id)
      if (status) {
        this.companyObjectives = this.companyObjectives.filter(objective => objective.id !== id)
        void Alert({
          message: Format('admin_global_deleted'),
          type: 'success',
        })
      }
      return status
    },
    // #endregion

    // #region Teams
    async CreateTeam(payload: IPayloadTeam) {
      const { status, data } = await companyAPI.PerformanceServices.CreateTeam(payload)
      if (status) {
        this.teams.unshift(data)
        void Alert({
          message: Format('admin_global_created'),
          type: 'success',
        })
      }

      return status
    },

    async UpdateTeam(payload: IPayloadTeam) {
      const { status } = await companyAPI.PerformanceServices.UpdateTeam(payload)

      if (status) {
        const index = this.teams.findIndex(team => team.id === payload.id)
        if (index !== -1) {
          const team = this.teams[index]
          if (team) {
            Object.assign(team, payload)
          }
        }
        void Alert({
          message: Format('admin_global_updated'),
          type: 'success',
        })
      }
      return status
    },

    async GetTeamList() {
      const { status, data } = await companyAPI.PerformanceServices.GetTeamList()

      this.teams = status ? data : []
      return status
    },

    async DeleteTeam(id: string) {
      const { status } = await companyAPI.PerformanceServices.DeleteTeam(id)

      if (status) {
        this.teams = this.teams.filter(team => team.id !== id)
        void Alert({
          message: Format('admin_global_deleted'),
          type: 'success',
        })
      }
      return status
    },
    // #endregion

    // #region Objective
    async GetObjectiveList(payload?: IPerformanceList) {
      const { status, data } = await companyAPI.PerformanceServices.GetObjectiveList(payload)

      this.objectives = status ? data : []
      return status
    },
    // #endregion

    // #region Team Members
    async UpdateTeamMembers(payload: IUpdateTeamMember) {
      const { status } = await companyAPI.PerformanceServices.UpdateTeamMembers(payload)

      if (status) {
        void Alert({
          message: Format('admin_global_updated'),
          type: 'success',
        })
      }
      return status
    },

    async GetTeamMembers(payload: IGetTeamMember) {
      const { status, data } = await companyAPI.PerformanceServices.GetTeamMembers(payload)

      this.teamMembers = status ? data : []
      return this.teamMembers
    },
    // #endregion

    // #region Performance Report
    async GetPerformanceBestTeam(payload: IGetPerformanceBestTeam) {
      const { data, status } = await reportAPI.ReportServices.GetPerformanceBestTeam(payload)
      return status ? data : []
    },

    async GetPerformanceBestTeamExcel(payload: IGetPerformanceBestTeam) {
      await reportAPI.ReportServices.GetPerformanceBestTeamExcel(payload)
    },

    async GetPerformanceEmployeeSuccess(payload: IGetPerformanceEmployeeSuccess) {
      const { data, status } = await reportAPI.ReportServices.GetPerformanceEmployeeSuccess(payload)
      return status ? data : []
    },

    async GetPerformanceEmployeeSuccessExcel(payload: IGetPerformanceEmployeeSuccess) {
      await reportAPI.ReportServices.GetPerformanceEmployeeSuccessExcel(payload)
    },

    async GetPerformanceCompanyProgress(payload: IGetCompanyProgress) {
      const { data, status } = await reportAPI.ReportServices.GetPerformanceCompanyProgress(payload)
      return status
        ? data
        : {
            totalObjectives: 0,
            completedObjectives: 0,
            progress: 0,
            improgressiveCount: 0,
            onTheRoadCount: 0,
            notStartedCount: 0,
            riskyCount: 0,
          }
    },

    async GetPerformanceWeeklyItemReport(payload: IGetPerformanceWeeklyItemReport) {
      const { data, status } =
        await reportAPI.ReportServices.GetPerformanceWeeklyItemReport(payload)
      return status ? data : []
    },

    async GetPerformanceObjectiveProgress(payload: IGetObjectiveProgress) {
      const { data, status } =
        await reportAPI.ReportServices.GetPerformanceObjectiveProgress(payload)
      return status ? data : []
    },

    async GetPerformanceCards(payload: IGetPerformanceReportCards) {
      const { data, status } = await reportAPI.ReportServices.GetPerformanceCards(payload)
      return status
        ? data
        : { totalProgress: 0, objectiveCount: 0, assignedWeeklyItemPercentage: 0, successRate: 0 }
    },

    // #endregion
  },
})
